import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { X, Mail, Hash, ArrowRight } from 'lucide-react';
import Button from '../Button';
import Input from '../Input';
import useSwipeToClose from '../hooks/useSwipeToClose';
import './EditOrderLogin.css';
import { drawerDurationMs } from '../utils/cssDuration';

/**
 * EditOrderLogin
 *
 * Login popup that lets a customer access the "Edit order" flow by entering
 * the email used at checkout + their order number.
 *
 * Mirrors the SchoolSelector modal pattern:
 *  - Desktop → centered popup (optional banner image on the left)
 *  - Mobile  → bottom-sheet drawer with swipe-to-dismiss
 *
 * Figma node: 4052:17465
 */

const DEFAULT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_ORDER_REGEX = /^[A-Za-z0-9-]{3,}$/;

const EditOrderLogin = ({
  isOpen = false,
  onClose,
  onSubmit,
  onSuccess,
  apiBrandSlug = null,
  /** Host-provided login: async (apiBrandSlug, {email, orderNumber}) → {token, order}.
   *  The design system has no backend client of its own. */
  onLogin = null,
  banner = null,

  title = 'Edit order',
  infoText = 'You can find your order number in the confirmation email sent to you.',

  emailLabel = 'Email',
  orderNumberLabel = 'Order number',
  submitLabel = 'Continue',

  emailErrorMessage = 'Please enter a valid email address',
  orderNumberErrorMessage = 'Please enter a valid order number',
  notFoundMessage = 'We couldn\'t find an order with that email and order number.',

  validateEmail,
  validateOrderNumber,

  initialEmail = '',
  initialOrderNumber = '',

  preview = false,
  lockScroll = true,
  className = '',
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [orderNumber, setOrderNumber] = useState(initialOrderNumber);
  const [emailError, setEmailError] = useState('');
  const [orderNumberError, setOrderNumberError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isClosing, setIsClosing] = useState(false);
  const [isEntered, setIsEntered] = useState(false);

  const overlayRef = useRef(null);
  const emailInputRef = useRef(null);
  const enteredTimer = useRef(null);
  const closeTimer = useRef(null);
  const focusTimer = useRef(null);

  /* ── Reset transient state whenever the popup is (re)opened ── */
  useEffect(() => {
    if (preview) return undefined;
    if (isOpen) {
      setEmail(initialEmail);
      setOrderNumber(initialOrderNumber);
      setEmailError('');
      setOrderNumberError('');
      setSubmitError('');
      setIsSubmitting(false);
      setIsClosing(false);

      if (lockScroll) {
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      }

      enteredTimer.current = setTimeout(() => setIsEntered(true), drawerDurationMs() + 50);
      focusTimer.current = setTimeout(() => {
        if (window.innerWidth >= 1024) emailInputRef.current?.focus?.();
      }, 300);
    } else {
      setIsEntered(false);
      if (lockScroll) {
        document.body.style.overflow = '';
        document.documentElement.style.overflow = '';
      }
    }

    return () => {
      clearTimeout(enteredTimer.current);
      clearTimeout(focusTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, preview, lockScroll]);

  useEffect(() => () => {
    clearTimeout(closeTimer.current);
    if (lockScroll && !preview) {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
  }, [lockScroll, preview]);

  const animateClose = useCallback(() => {
    if (preview) return;
    setIsClosing(true);
    closeTimer.current = setTimeout(() => {
      setIsClosing(false);
      if (onClose) onClose();
    }, drawerDurationMs());
  }, [onClose, preview]);

  const { containerRef: mobileDrawerRef, dragOffset, isDragging, DragHandle } = useSwipeToClose(
    animateClose,
    isOpen && !preview,
  );

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) animateClose();
  };

  /* ── ESC to close ── */
  useEffect(() => {
    if (preview || !isOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape') animateClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, preview, animateClose]);

  const handleSubmit = async () => {
    const isEmailValid = validateEmail ? validateEmail(email) : DEFAULT_EMAIL_REGEX.test(email.trim());
    const isOrderValid = validateOrderNumber
      ? validateOrderNumber(orderNumber)
      : DEFAULT_ORDER_REGEX.test(orderNumber.trim());

    setEmailError(isEmailValid ? '' : emailErrorMessage);
    setOrderNumberError(isOrderValid ? '' : orderNumberErrorMessage);
    setSubmitError('');

    if (!isEmailValid || !isOrderValid) return;

    const cleaned = { email: email.trim(), orderNumber: orderNumber.trim() };

    // When an API brand slug is provided, authenticate against the backend and
    // hand the caller the edit token + order on success. Otherwise fall back to
    // the legacy onSubmit handoff (used by the design-system preview).
    if (apiBrandSlug && onSuccess && onLogin) {
      setIsSubmitting(true);
      try {
        const { token, order } = await onLogin(apiBrandSlug, cleaned);
        setIsSubmitting(false);
        onSuccess({ ...cleaned, token, order });
      } catch (err) {
        setIsSubmitting(false);
        setSubmitError(err?.status === 422 ? notFoundMessage : (err?.message || notFoundMessage));
      }
      return;
    }

    if (onSubmit) onSubmit(cleaned);
  };

  if (!isOpen && !isClosing && !preview) return null;

  const drawerStyle = isDragging ? { transform: `translateY(${dragOffset}px)` } : undefined;

  const renderContent = () => (
    <div className="ds-edit-order-login__container">
      {/* Close button */}
      <Button
        size="tertiary"
        variant="text"
        iconOnly
        iconLeft={<X size={20} />}
        aria-label="Close"
        className="ds-edit-order-login__close"
        onClick={animateClose}
      />

      <div className="ds-edit-order-login__wrapper">
        {/* Top: title + info */}
        <div className="ds-edit-order-login__top">
          <h2 className="ds-edit-order-login__title typo-h3">{title}</h2>
          {infoText && (
            <div className="ds-edit-order-login__info">
              <p className="ds-edit-order-login__info-text typo-body">{infoText}</p>
            </div>
          )}
        </div>

        {/* Form */}
        <div className="ds-edit-order-login__form">
          <div className="ds-edit-order-login__inputs">
            <Input
              ref={emailInputRef}
              type="text"
              label={emailLabel}
              icon={<Mail size={20} />}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError('');
              }}
              error={emailError}
              autoComplete="email"
              inputMode="email"
              className="ds-edit-order-login__email-input"
            />

            <Input
              type="text"
              label={orderNumberLabel}
              icon={<Hash size={20} />}
              value={orderNumber}
              onChange={(e) => {
                setOrderNumber(e.target.value);
                if (orderNumberError) setOrderNumberError('');
              }}
              error={orderNumberError}
              className="ds-edit-order-login__order-input"
            />
          </div>

          {submitError && (
            <p className="ds-edit-order-login__submit-error typo-body-sm" role="alert">
              {submitError}
            </p>
          )}

          <Button
            size="primary"
            variant="main"
            iconRight={<ArrowRight size={20} />}
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="ds-edit-order-login__submit"
          >
            {isSubmitting ? '…' : submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div
      ref={overlayRef}
      className={`ds-edit-order-login__overlay ${isClosing ? 'ds-edit-order-login__overlay--closing' : ''} ${preview ? 'ds-edit-order-login__overlay--preview' : ''} ${className}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* ── Desktop: centered popup ── */}
      <div
        className={`ds-edit-order-login ds-edit-order-login--desktop ${isClosing ? 'ds-edit-order-login--closing' : ''} ${isEntered ? 'ds-edit-order-login--entered' : ''}`}
      >
        {banner && (
          <div className="ds-edit-order-login__banner">
            <img src={banner} alt="" className="ds-edit-order-login__banner-img" />
          </div>
        )}

        <div className="ds-edit-order-login__content">
          {renderContent()}
        </div>
      </div>

      {/* ── Mobile: bottom sheet ── */}
      <div
        ref={mobileDrawerRef}
        className={`ds-edit-order-login ds-edit-order-login--mobile ${isClosing ? 'ds-edit-order-login--closing' : ''} ${isEntered ? 'ds-edit-order-login--entered' : ''}`}
        style={drawerStyle}
      >
        <DragHandle className="ds-edit-order-login__swipe-handle" />

        <div className="ds-edit-order-login__content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

EditOrderLogin.propTypes = {
  /** Whether the popup/drawer is open */
  isOpen: PropTypes.bool,
  /** Callback when the popup is closed */
  onClose: PropTypes.func,
  /** Legacy callback with { email, orderNumber } when submitted & valid (preview/no-API) */
  onSubmit: PropTypes.func,
  /** Callback with { email, orderNumber, token, order } after a successful API login */
  onSuccess: PropTypes.func,
  onLogin: PropTypes.func,
  /** Backend brand slug (e.g. 'clsdk'); when set, submit authenticates via the API */
  apiBrandSlug: PropTypes.string,
  /** Message shown when the email + order number don't match an order */
  notFoundMessage: PropTypes.string,
  /** Optional banner image (desktop only) */
  banner: PropTypes.string,
  /** Modal title */
  title: PropTypes.string,
  /** Info text displayed below the title */
  infoText: PropTypes.string,
  /** Email input label */
  emailLabel: PropTypes.string,
  /** Order number input label */
  orderNumberLabel: PropTypes.string,
  /** Submit button label */
  submitLabel: PropTypes.string,
  /** Error message for an invalid email */
  emailErrorMessage: PropTypes.string,
  /** Error message for an invalid order number */
  orderNumberErrorMessage: PropTypes.string,
  /** Override email validation: (value) => boolean */
  validateEmail: PropTypes.func,
  /** Override order number validation: (value) => boolean */
  validateOrderNumber: PropTypes.func,
  /** Initial email value */
  initialEmail: PropTypes.string,
  /** Initial order number value */
  initialOrderNumber: PropTypes.string,
  /** Static preview mode (no animation / portal behaviour) */
  preview: PropTypes.bool,
  /** Lock body scroll while open */
  lockScroll: PropTypes.bool,
  /** Additional class name on the overlay */
  className: PropTypes.string,
};

export default EditOrderLogin;
